import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { CheckCircle2, Loader2, Mail } from "lucide-react"
import { getMe } from "../api"
import { useCart } from "../cart"
import type { Me } from "../types"

type Phase = "pending" | "active" | "timeout"

/** Freischaltung passiert serverseitig via Webhook. Für eingeloggte Käufer pollen
 *  wir /billing/me, bis das Entitlement erscheint (max. ~5x, 2s Abstand).
 *  Gäste (guest=1 in der URL) sind NICHT eingeloggt -> kein Poll möglich; sie
 *  bekommen einen statischen Hinweis, ihr Konto per E-Mail zu aktivieren. */
export function CheckoutSuccess({ authed }: { authed: boolean }) {
  const { clear } = useCart()
  const isGuest =
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("guest") === "1"
  const [phase, setPhase] = useState<Phase>("pending")
  const [me, setMe] = useState<Me | null>(null)
  const cleared = useRef(false)

  useEffect(() => {
    if (!cleared.current) {
      clear()
      cleared.current = true
    }
  }, [clear])

  useEffect(() => {
    // Gäste nicht pollen (nicht eingeloggt); nur eingeloggte Käufer.
    if (isGuest || !authed) return
    let alive = true
    let tries = 0
    const maxTries = 5

    const hasEntitlement = (m: Me): boolean =>
      Boolean(m.navigator_active) || (m.infopackages?.length ?? 0) > 0

    async function poll() {
      tries += 1
      try {
        const m = await getMe()
        if (!alive) return
        setMe(m)
        if (hasEntitlement(m)) {
          setPhase("active")
          return
        }
      } catch {
        /* weiter versuchen */
      }
      if (!alive) return
      if (tries >= maxTries) {
        setPhase("timeout")
        return
      }
      window.setTimeout(poll, 2000)
    }

    poll()
    return () => {
      alive = false
    }
  }, [authed, isGuest])

  // Gast-Variante: statischer Hinweis auf die Konto-Aktivierung per E-Mail.
  if (isGuest) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <Mail className="mx-auto h-12 w-12 text-brand-blue" />
        <h1 className="mt-4 text-2xl font-semibold">Zahlung erhalten – vielen Dank!</h1>
        <p className="mt-2 text-brand-muted">
          Wir haben Ihnen eine E-Mail geschickt, über die Sie Ihr Konto aktivieren: E-Mail
          bestätigen und ein Passwort setzen. Danach melden Sie sich im Navigator an.
        </p>
        <p className="mt-2 text-sm text-brand-muted">
          Bitte verwenden Sie dieselbe E-Mail-Adresse, mit der Sie im Checkout bezahlt haben. Falls
          die E-Mail nicht innerhalb weniger Minuten ankommt, prüfen Sie den Spam-Ordner oder melden
          Sie sich beim SINUS-Institut.
        </p>

        <div className="mt-8 flex items-center justify-center gap-3">
          <a
            href="https://navigator.sinus-institut.de"
            className="rounded-full bg-brand-blue px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
          >
            Zum Navigator
          </a>
          <Link
            to="/"
            className="rounded-full border border-black/10 px-4 py-2 text-sm hover:bg-surface"
          >
            Weiter einkaufen
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-xl px-4 py-16 text-center">
      {phase === "active" ? (
        <>
          <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
          <h1 className="mt-4 text-2xl font-semibold">Vielen Dank!</h1>
          <p className="mt-2 text-brand-muted">
            Ihr Kauf ist eingegangen und Ihr Zugang ist freigeschaltet
            {me?.valid_until ? ` (gültig bis ${me.valid_until})` : ""}.
          </p>
        </>
      ) : phase === "timeout" ? (
        <>
          <CheckCircle2 className="mx-auto h-12 w-12 text-brand-muted" />
          <h1 className="mt-4 text-2xl font-semibold">Zahlung erhalten</h1>
          <p className="mt-2 text-brand-muted">
            Die Freischaltung läuft und ist gleich abgeschlossen. Falls sie nicht innerhalb weniger
            Minuten erscheint, melden Sie sich beim SINUS-Institut.
          </p>
        </>
      ) : (
        <>
          <Loader2 className="mx-auto h-12 w-12 animate-spin text-brand-muted" />
          <h1 className="mt-4 text-2xl font-semibold">Bestellung erhalten</h1>
          <p className="mt-2 text-brand-muted">Freischaltung wird vorbereitet …</p>
        </>
      )}

      <div className="mt-8 flex items-center justify-center gap-3">
        <a
          href="https://navigator.sinus-institut.de"
          className="rounded-full bg-brand-blue px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
        >
          Zum Navigator
        </a>
        <Link
          to="/"
          className="rounded-full border border-black/10 px-4 py-2 text-sm hover:bg-surface"
        >
          Weiter einkaufen
        </Link>
      </div>
    </div>
  )
}
